import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { axios } from "@moonscape/shared";

export interface InboxMessage {
  id: number;
  thread_id: number;
  sender_type: "admin" | "user";
  body: string;
  read_at: string | null;
  created_at: string;
  // Client-only: present on optimistically-added messages until the
  // server confirms (cleared on refetch) or the send fails.
  status?: "sending" | "failed";
}

export interface InboxResponse {
  messages: InboxMessage[];
}

const INBOX_LIST_KEY = ["inbox", undefined] as const;
let optimisticIdCounter = 0;

export function useInbox(cursor?: number) {
  return useQuery({
    queryKey: ["inbox", cursor],
    queryFn: () =>
      axios
        .get<InboxResponse>("/inbox", { params: cursor ? { cursor } : undefined })
        .then((r) => r.data),
  });
}

export function useInboxUnreadCount() {
  return useQuery({
    queryKey: ["inbox", "unread"],
    queryFn: () =>
      axios.get<{ count: number }>("/inbox/unread-count").then((r) => r.data.count),
    refetchInterval: 60_000,
  });
}

export function useSendInboxReply() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: string) =>
      axios.post("/inbox/messages", { body }).then((r) => r.data),
    onMutate: async (body: string) => {
      // Cancel any in-flight refetches so they don't clobber our optimistic write
      await qc.cancelQueries({ queryKey: ["inbox"] });
      const tempId = -Date.now() - ++optimisticIdCounter;
      const optimistic: InboxMessage = {
        id: tempId,
        thread_id: 0,
        sender_type: "user",
        body,
        read_at: null,
        created_at: new Date().toISOString(),
        status: "sending",
      };
      qc.setQueryData<InboxResponse>(INBOX_LIST_KEY, (old) => ({
        messages: [optimistic, ...(old?.messages ?? [])],
      }));
      return { tempId };
    },
    onError: (_err, _body, ctx) => {
      if (!ctx) return;
      qc.setQueryData<InboxResponse>(INBOX_LIST_KEY, (old) => ({
        messages: (old?.messages ?? []).map((m) =>
          m.id === ctx.tempId ? { ...m, status: "failed" as const } : m
        ),
      }));
    },
    onSuccess: () => {
      // Refetch — server returns the canonical message and drops the temp row
      qc.invalidateQueries({ queryKey: ["inbox"] });
    },
  });
}

export function useMarkInboxRead() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => axios.post("/inbox/read").then((r) => r.data),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["inbox", "unread"] });
    },
  });
}
